import { Input, InputProps } from '@fluentui/react-components';
import { DismissRegular, SearchRegular } from '@fluentui/react-icons';
import cn from 'classnames';
import React, { forwardRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useInputAppearance } from '../hooks';
import './SearchInput.css';

export interface SearchInputProps extends Omit<InputProps, 'contentAfter' | 'type'> {
  /** Show a clear button when the input has a value. Defaults to true. */
  clearable?: boolean;
  /** Callback when the clear button is clicked */
  onClear?: () => void;
  /** Aria-label for the clear button. Overrides the default translated label. */
  clearAriaLabel?: string;
}

/**
 * Search input with search icon (right) and optional clear button.
 * Drop-in replacement for the Northstar `Input` with `icon={<SearchIcon />}` and `clearable`.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      clearable = true,
      onClear,
      onChange,
      value,
      className,
      clearAriaLabel,
      appearance,
      'aria-label': ariaLabel,
      ...rest
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const themedAppearance = useInputAppearance();
    const resolvedAppearance = appearance ?? themedAppearance;

    const handleClear = useCallback(() => {
      onClear?.();
    }, [onClear]);

    const showClear = clearable && value && String(value).length > 0;

    return (
      <Input
        ref={ref}
        type="text"
        appearance={resolvedAppearance}
        className={cn('aix-search-input', `aix-search-input--${resolvedAppearance}`, className)}
        value={value}
        onChange={onChange}
        aria-label={
          ariaLabel ??
          t('pc:searchInput:searchInput:search_aria_label', {
            defaultValue: 'Suchen',
          })
        }
        contentAfter={
          showClear ? (
            <button
              type="button"
              className="aix-search-input__clear"
              onClick={handleClear}
              aria-label={
                clearAriaLabel ??
                t('pc:searchInput:searchInput:clear_aria_label', {
                  defaultValue: 'Sucheingabe löschen',
                })
              }
            >
              <DismissRegular />
            </button>
          ) : (
            <SearchRegular />
          )
        }
        {...rest}
      />
    );
  },
);

SearchInput.displayName = 'SearchInput';
